/**
 * Cross-pool sweep — the fetch half (Relationship Detection Assist PRD v2,
 * phase R3, §5).
 *
 * Detection maths: src/lib/crossPoolSweepCore.ts.
 *
 * READ-ONLY, with one deliberate exception that is not a write of its own:
 * `proposeSweepFinding` calls the EXISTING `createNexusProposal`, which is how
 * every agent in this system surfaces a candidate. It is invoked by a person
 * clicking on a specific finding — the sweep itself never writes. That keeps
 * the posture the whole program runs on: detection proposes, authority
 * disposes, and here even the proposing is human-initiated.
 *
 * Pools are loaded through `loadPoolScopeOptions`, the loader `poolViolations`
 * already uses, rather than a second query of `item_pools.priority_ids`. Pool
 * membership having two readings in one codebase is precisely the drift the
 * repo's reuse rule exists to prevent, and a sweep that disagreed with the
 * anti-pattern detector about who is in a pool would be worse than no sweep.
 */

import { getSupabaseClient } from './supabaseClient';
import { loadPoolScopeOptions } from './poolViolations';
import { createNexusProposal } from './nexusAgentService';
import {
  runCrossPoolSweep,
  type CrossPoolSweepReport,
  type SweepClaim,
  type SweepEdge,
  type SweepFinding,
  type SweepPool,
} from './crossPoolSweepCore';

/**
 * `proposed_by_agent` for candidates this sweep raises.
 *
 * A distinct name, following the `excursion_return` precedent already in prod:
 * R0's instrumentation groups by agent, so cross-pool findings get their own
 * confirmation rate instead of being averaged into Nexus's. A sweep that is
 * mostly noise must be visible as such without dragging the batch agent's
 * number down with it.
 */
export const CROSS_POOL_SWEEP_AGENT = 'cross_pool_sweep';

export interface CrossPoolSweepResult extends CrossPoolSweepReport {
  /** Pools with their names, for labelling findings at the surface. */
  pools: SweepPool[];
  /** Item id -> title, for every item named in a finding. */
  titles: Record<string, string>;
  error: string | null;
}

const EMPTY: CrossPoolSweepResult = {
  findings: [],
  poolsSwept: 0,
  capabilitySubstrateEmpty: true,
  pools: [],
  titles: {},
  error: null,
};

interface EdgeRow {
  source_item_id: string;
  target_item_id: string;
  relationship_type: string;
  subtype: string | null;
  status: string | null;
}

interface ClaimRow {
  item_id: string;
  resource_type: string | null;
  resource_name: string | null;
  status: string;
}

/**
 * Sweep a workspace's active pools for shared prerequisites and shared scarce
 * capabilities.
 *
 * The three reads run together; a failure in any one is reported rather than
 * absorbed, because "the sweep found nothing" and "the sweep could not read
 * the edges" look identical on a screen and mean opposite things.
 */
export async function runWorkspaceCrossPoolSweep(
  workspaceId: string,
): Promise<CrossPoolSweepResult> {
  if (!workspaceId) return EMPTY;

  const supabase = getSupabaseClient();

  const [poolOptions, edgeRes, claimRes] = await Promise.all([
    loadPoolScopeOptions(workspaceId),
    supabase
      .from('item_relationship_edges')
      .select('source_item_id, target_item_id, relationship_type, subtype, status')
      .eq('workspace_id', workspaceId),
    supabase
      .from('plan_resource_claims')
      .select('item_id, resource_type, resource_name, status')
      .eq('workspace_id', workspaceId)
      .eq('claim_kind', 'capacity_hours'),
  ]);

  const error = edgeRes.error?.message ?? claimRes.error?.message ?? null;

  const pools: SweepPool[] = poolOptions.map((p) => ({
    id: p.id,
    name: p.name,
    cycleId: p.cycleId,
    itemIds: p.itemIds,
  }));

  const edges: SweepEdge[] = ((edgeRes.data ?? []) as EdgeRow[]).map((row) => ({
    sourceItemId: row.source_item_id,
    targetItemId: row.target_item_id,
    type: row.relationship_type,
    subtype: row.subtype,
    status: row.status,
  }));

  const claims: SweepClaim[] = ((claimRes.data ?? []) as ClaimRow[]).map((row) => ({
    itemId: row.item_id,
    resourceType: row.resource_type,
    resourceName: row.resource_name,
    status: row.status,
  }));

  const report = runCrossPoolSweep({ pools, edges, claims });
  const titles = await fetchTitlesForFindings(report.findings);

  return { ...report, pools, titles, error };
}

/**
 * Titles for every item a finding names — the members AND the shared
 * prerequisite, which is frequently in no pool and so appears in no other
 * lookup the caller holds.
 */
async function fetchTitlesForFindings(findings: SweepFinding[]): Promise<Record<string, string>> {
  const ids = new Set<string>();
  for (const f of findings) {
    for (const m of f.members) ids.add(m.itemId);
    if (f.kind === 'shared_prerequisite') ids.add(f.sharedKey);
  }
  if (ids.size === 0) return {};

  const supabase = getSupabaseClient();
  const { data, error } = await supabase.from('items').select('id, title').in('id', [...ids]);
  if (error || !data) return {};

  const titles: Record<string, string> = {};
  for (const row of data as Array<{ id: string; title: string | null }>) {
    titles[row.id] = row.title ?? row.id;
  }
  return titles;
}

/**
 * Raise one cross-pool pair as a `related` candidate, for human review in the
 * queue that already exists.
 *
 * `related` because "both of these need X" is a real structural fact about two
 * items that is not a dependency between them, not a package, and not a
 * cluster — and inventing a type for it is a canon 07A/12 conversation, not a
 * schema addition here (PRD non-goals). The proposal lands `active` and stays
 * a candidate until a human accepts it through `accept_nexus_proposal`.
 */
export async function proposeSweepFinding(
  workspaceId: string,
  pair: { sourceItemId: string; targetItemId: string; rationale: string },
): Promise<{ error: string | null }> {
  const { error } = await createNexusProposal({
    workspaceId,
    sourceItemId: pair.sourceItemId,
    targetItemId: pair.targetItemId,
    relationshipType: 'related',
    directionality: 'undirected',
    rationale: pair.rationale,
    // Structural co-incidence, not a semantic judgement: the sweep knows the
    // two items share a constraint, not that the team should treat them as
    // one. `medium` is the honest self-assessment and keeps R0's calibration
    // slices meaningful.
    confidence: 'medium',
    proposedByAgent: CROSS_POOL_SWEEP_AGENT,
  });
  return { error };
}

/**
 * Resolve a pool- or cycle-scoped run to the item ids it covers.
 *
 * This is what makes the new `pool` / `cycle` scope types real for the batch
 * runner: a session wants proposals for ITS pool, and until now the runner
 * could only offer "the whole workspace, by date".
 */
export async function resolveScopeItemIds(
  workspaceId: string,
  scope: { type: string; poolId?: string; cycleId?: string },
): Promise<string[] | null> {
  if (scope.type !== 'pool' && scope.type !== 'cycle') return null;

  const options = await loadPoolScopeOptions(workspaceId);
  const matching =
    scope.type === 'pool'
      ? options.filter((o) => o.id === scope.poolId || o.id === `pool:${scope.poolId}`)
      : options.filter((o) => o.cycleId && o.cycleId === scope.cycleId);

  const ids = new Set<string>();
  for (const o of matching) for (const id of o.itemIds) ids.add(id);
  return [...ids];
}
